/**
 * Credit display utility functions.
 *
 * Formatting helpers for credit consumption, audio duration,
 * and prediction accuracy in the job history UI.
 *
 * Requirements: 9.1, 9.2, 9.3, 11.5
 */

/**
 * Format credits consumed with 1 decimal place.
 * Returns "—" for legacy jobs without credits_consumed.
 */
export function formatCredits(credits: number | undefined | null): string {
    if (credits == null) return '—';
    return credits.toFixed(1);
}

/**
 * Format a credit balance for dashboard display.
 *
 * Rounds to 1 decimal place to hide floating-point artifacts (e.g.
 * subtracting 880.4 from 900 yields 19.600000000000023 in JavaScript)
 * and drops trailing zeros so "880.0" renders as "880".
 *
 * Use this for any user-facing balance/total/used number on the
 * dashboard, navbar, profile menu, etc.
 */
export function formatCreditsBalance(credits: number | undefined | null): string {
    if (credits == null) return '—';
    const rounded = Math.round(credits * 10) / 10;
    return Number.isInteger(rounded) ? rounded.toString() : rounded.toString();
}

/**
 * Format audio duration in seconds as mm:ss.
 * e.g., 125 → "2:05", 60 → "1:00", 5 → "0:05"
 * Returns "N/A" if duration is undefined/null.
 */
export function formatAudioDuration(seconds: number | undefined | null): string {
    if (seconds == null) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs.toString().padStart(2, '0')}s`;
}

/**
 * Format the credit calculation method for display.
 * "audio_duration" → "Audio Duration", "word_count" → "Word Count"
 */
export function formatCalculationMethod(method: string | undefined | null): string {
    if (!method) return 'N/A';
    return method
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}
